/**
 * FTR4: GLB body meshes + floating-origin placement.
 *
 * Each body's authoritative position is Float64 (sim-core World). The render
 * position is the Float32 DELTA from the ship's Float64 world position:
 *     renderPos = (bodyWorldPos - shipWorldPos)   // fits in Float32 near camera
 * The worldGroup itself stays at the origin; we set each body group's local
 * position to that delta. This keeps the ship at (0,0,0) in render space.
 *
 * GLB meshes are authored at Blender display scale, so we normalise each to a
 * unit sphere then scale to the body's true radius (km == sim units).
 */
import * as THREE from 'three';
import type { Vec3, BodyTextureSet } from '@blackmarble/sim-core';
import type { World, WorldBody } from '../sim/world.js';
import { upgradeToHiRes } from './planetMaterials.js';
import { buildBodyGroup } from './bodyFactory.js';

interface BodyVisual {
  body: WorldBody;
  group: THREE.Group;
  clouds?: THREE.Mesh; // Earth cloud shell (rotates slowly)
  atmosphere?: THREE.Mesh; // Earth atmosphere shell (hidden when camera inside)
  atmosphereRadiusKm?: number;
  texMesh?: THREE.Mesh; // textured sphere eligible for lazy hi-res upgrade
  tex?: BodyTextureSet;
  upgraded?: boolean; // lazy hi-res already swapped in
}

const HI_RES_UPGRADE_KM = 3_000_000; // swap placeholder -> hi-res within this range

const TWO_PI = Math.PI * 2;

export class BodyRenderer {
  private readonly visuals: BodyVisual[] = [];
  private readonly solPos: Vec3; // Float64 world position of Sol (origin)

  constructor(
    private readonly world: World,
    private readonly parent: THREE.Group,
  ) {
    this.solPos = world.get('Sol')?.worldPos ?? { x: 0, y: 0, z: 0 };
  }

  async loadAll(baseUrl = '/assets/bodies/'): Promise<void> {
    await Promise.all(this.world.bodies.map((body) => this.loadBody(body, baseUrl)));
  }

  private async loadBody(body: WorldBody, baseUrl: string): Promise<void> {
    // Single source of truth (shared with the asset viewer): bodyFactory.
    const built = await buildBodyGroup(body.entry, baseUrl);
    this.parent.add(built.group);
    const visual: BodyVisual = { body, group: built.group };
    if (body.entry.tex) visual.tex = body.entry.tex;
    if (built.atmosphere) visual.atmosphere = built.atmosphere;
    if (built.atmosphereRadiusKm !== undefined) visual.atmosphereRadiusKm = built.atmosphereRadiusKm;
    if (built.clouds) visual.clouds = built.clouds;
    if (built.texMesh) visual.texMesh = built.texMesh;
    this.visuals.push(visual);
  }

  /**
   * Place every body relative to the ship (floating origin) and spin it.
   * @param shipWorldPos Float64 authoritative ship position
   * @param t            sim time (s) for axial rotation
   */
  update(shipWorldPos: Vec3, t: number): void {
    // FTR7: inline the Float64 delta — no Vec3 allocation per body per frame.
    for (const v of this.visuals) {
      const wp = v.body.worldPos;
      const dx = wp.x - shipWorldPos.x;
      const dy = wp.y - shipWorldPos.y;
      const dz = wp.z - shipWorldPos.z;
      v.group.position.set(dx, dy, dz);
      const rp = v.body.entry.rotationPeriod;
      if (rp > 0) v.group.rotation.y = ((t / rp) % 1) * TWO_PI;
      // Earth clouds drift slightly faster than the surface.
      if (v.clouds) v.clouds.rotation.y = ((t / (rp * 0.85)) % 1) * TWO_PI;

      const distKm = Math.sqrt(dx * dx + dy * dy + dz * dz);
      this.updateAtmosphere(v, distKm);

      // VFX6: lazy hi-res upgrade when the ship gets near a distant body.
      if (v.tex?.lazy && !v.upgraded && v.texMesh && distKm < HI_RES_UPGRADE_KM) {
        upgradeToHiRes(v.texMesh, v.tex);
        v.upgraded = true;
      }
    }
  }

  /**
   * VFX6/VFX8: hide the atmosphere shell when the camera is inside it (no wash),
   * and feed the world-space planet->Sol direction to the scattering shader.
   */
  private updateAtmosphere(v: BodyVisual, distKm: number): void {
    if (!v.atmosphere || v.atmosphereRadiusKm === undefined) return;
    v.atmosphere.visible = distKm > v.atmosphereRadiusKm;
    const wp = v.body.worldPos;
    const sx = this.solPos.x - wp.x;
    const sy = this.solPos.y - wp.y;
    const sz = this.solPos.z - wp.z;
    const sl = Math.sqrt(sx * sx + sy * sy + sz * sz) || 1;
    const atmoMat = v.atmosphere.material as THREE.ShaderMaterial;
    const u = atmoMat.uniforms;
    if (u.sunDir) (u.sunDir.value as THREE.Vector3).set(sx / sl, sy / sl, sz / sl);
  }

  /** World-space (render) position of a named body, for HUD/target maths. */
  bodyRenderPos(name: string, out: THREE.Vector3): boolean {
    const v = this.visuals.find((x) => x.body.entry.name === name);
    if (!v) return false;
    out.copy(v.group.position);
    return true;
  }
}
